/**
 * Thanksgiving doodles — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingDoodlesPack: ToilePack = {
    slug: 'thanksgiving-doodles',
    category: 'season',
    name: 'Thanksgiving doodles',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'mixed', label: 'Mixed doodles', subject: 'A page packed with small thanksgiving doodles: an acorn, a leaf, a pie, corn, a turkey, a pumpkin, a candle, no gaps'},
        {key: 'food', label: 'Feast doodles', subject: 'A page of feast doodles: a roast, mashed potatoes, a gravy boat, rolls, cranberries, pies, packed tight'},
        {key: 'autumn', label: 'Autumn doodles', subject: 'A page of autumn doodles: leaves of every shape, acorns, mushrooms, a scarf, a mug, packed tight'},
    ],
    styles: [
        {key: 'bold', label: 'Bold doodles', style: 'bold marker-doodle outline, thick confident uniform strokes', palette: 'Black ink only', framing: 'Packed edge to edge across the page with a thin white margin, no gaps between doodles'},
        {key: 'fine', label: 'Fine line doodles', style: 'fine pen doodle line, thin delicate uniform strokes', palette: 'Black ink only', framing: 'Neatly spaced across the page with a small white margin'},
        {key: 'bubble', label: 'Bubble doodles', style: 'chunky bubble-outline doodle line, thick rounded uniform strokes with soft corners', palette: 'Black ink only', framing: 'Arranged in a loose grid across the page with a white margin'},
    ],
    directions: ['a single large doodle', 'in a bullet-journal grid', 'as a place-card design'],
    directionHint: 'try: a single large doodle instead',
    seo: {
        title: 'Thanksgiving doodles: mixed, feast, autumn',
        metaDescription: 'Nine Thanksgiving doodle sheets — mixed, feast, and autumn — packed edge to edge in bold, fine-line or bubble line, ready to color or trace.',
        h1: 'Thanksgiving doodles: mixed, feast and autumn sheets',
        tagline: 'Nine doodle sheets, three Thanksgiving themes, three line styles each — packed edge to edge, ready to color or trace.',
        intro: 'A doodle sheet packed edge to edge: acorns, a pie, corn, a turkey, a pumpkin, no gaps. These nine run three Thanksgiving themes — a mixed page, a feast spread of roast and pie, and an autumn page of leaves and acorns — in bold marker, fine pen, or chunky bubble line. Color the page, trace the shapes, or ask for a different theme.',
        useCases: [
            'a classroom filler activity',
            'a Thanksgiving-table coloring page for kids',
            'a fall-themed sticker or card craft',
            'a rainy-day printable for the holiday',
        ],
        faq: [
            {q: 'What size do the sheets print at?', a: 'Each sheet is drawn square with a white margin, so it prints clean on standard paper.'},
            {q: 'Can I color or trace the doodles?', a: 'Yes — every doodle is a clean closed outline with no shading, ready to color in or trace onto another project.'},
            {q: "What's the difference between the three line styles?", a: 'Bold uses thick confident marker strokes, fine line is a delicate pen outline, and bubble is a chunky rounded line — three ways a doodle sheet is drawn.'},
            {q: 'Can I get a different Thanksgiving theme?', a: "Yes — that's what the direction field is for. Type a different theme, like a single large doodle, and the sheet redraws in the same three line styles."},
        ],
    },
}
